/**
 * Editor multi-page-scroll cluster — multipage scaffold builder, per-page
 * controller wiring (drag/resize/draw/rubber-band/contextmenu), background
 * page-size fix-up, and the page-nav (prev/next/page-input) bindings.
 *
 * Extracted from editor.ts in Phase EB.3 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.3"
 * + §3 Cluster E + §9 Coupling-Knot 2 + §9.3 — coupling discoveries).
 *
 * Phase EB.3 ALSO consolidated the `renderOverlays / renderOverlaysForPage`
 * DRY-drift onto a shared `_renderOverlaysCore` helper in `editor-render.ts`.
 * `renderPageIntoFrame` and `renderOverlaysForPage` moved into editor-render
 * (natural home — both touch the per-canvas WeakMap singleton). This module
 * imports them via `editor-render.js`, so the multi-page manager's render
 * hooks are 1-line `.catch(...)` wrappers around the editor-render entry
 * points (no per-page render logic lives here).
 *
 * Cross-cluster coupling (post-EB.5; setter reduced from 5 → 1 binding):
 *
 *   `_wirePageControllers` wires per-page DragMoveController /
 *   ResizeMoveController / DrawCreateController callbacks that call BACK
 *   into the orchestrator:
 *     - `selectStruct(structId)` — Cluster H (editor.ts, orchestrator scope)
 *     - `dispatchContextAction(action, payload)` — Cluster F → direct import
 *     - `handleDragMoveCommit(spec)` — Cluster G (insert-flow) → direct import
 *     - `handleResizeCommit(spec)` — Cluster G (insert-flow) → direct import
 *     - `handleDrawCreateCommit(page, spec)` — Cluster G (insert-flow) → direct import
 *
 *   Phase EB.5 (Cluster F extraction) moved `dispatchContextAction` into
 *   `editor-card-pane.ts` and the three commit handlers (already in
 *   `editor-insert-flow.ts` since EB.4) are now direct imports. Only
 *   `selectStruct` still flows through the setter (still in editor.ts —
 *   orchestrator scope, touches `els.structList`).
 *
 *   editor.ts calls `setMultipageCallbackBindings({selectStruct})` once
 *   during boot, after `selectStruct` is defined and BEFORE
 *   `mountMultiPageScroll` fires. `_requireMultipageCbBindings` throws
 *   fail-fast if any per-page controller is constructed before bindings
 *   are installed — silent null would route every selectStruct call into
 *   the void.
 *
 * `state` and `els` come from `editor-state.js`. `renderOverlays`,
 * `renderPageIntoFrame`, `renderOverlaysForPage`, `fitToWidthScale`,
 * `_scheduleIdle`, `ZOOM_MIN`, `ZOOM_MAX` come from `editor-render.js`.
 * `buildPageBboxMap` comes from `editor-bbox-utils.js`.
 * Page-nav helpers (updatePageNavControls, goToPage, setMultipageCallbackBindings,
 * _requireMultipageCbBindings) live in `multipage-nav.ts`.
 *
 * Per the "Type-in-place before decomposing" rule (CLAUDE.md): the existing
 * `any` annotations on still-untyped surfaces (per-page-controller specs,
 * `_paneLayout` interactions) are preserved — tightening them is the
 * `ts-strict-zero` Epic's job.
 */
import { log } from "./log.js";
import { PDF_US_LETTER } from "./dom-utils.js";
import { openContextMenu } from "./context-menu.js";
import { DragMoveController } from "./drag-move.js";
import { ResizeMoveController } from "./resize-move.js";
import { RubberBandController } from "./rubber-band.js";
import { DrawCreateController } from "./mcid-reassociate.js";
import { buildPageFrames, MultiPageScrollManager, PageZone, } from "./multi-page-scroll.js";
import { state, els, prefetchRange, _bboxCache } from "./editor-state.js";
import { renderOverlaysForPage, renderPageIntoFrame, fitToWidthScale, _scheduleIdle, ZOOM_MIN, ZOOM_MAX, } from "./editor-render.js";
import { buildPageBboxMap } from "./editor-bbox-utils.js";
import { dispatchContextAction } from "./editor-card-pane.js";
import { handleDragMoveCommit, handleResizeCommit, handleDrawCreateCommit, canvasBboxToPdf, } from "./editor-insert-flow.js";
import { setMultipageCallbackBindings, _requireMultipageCbBindings, updatePageNavControls, goToPage, } from "./multipage-nav.js";
export { setMultipageCallbackBindings, _requireMultipageCbBindings, updatePageNavControls, goToPage };
// ─── Page-nav button + input event bindings ─────────────────────────────────
//
// Prev/Next, page-input, and sidebar mirror controls. Wired at module-evaluation
// time so the bindings are live before the user can interact with the toolbar
// (editor.ts imports this module above the workspace-mount path).
// Prev/Next button wiring.
els.prevPageBtn.addEventListener("click", () => { void goToPage(state.currentPage - 1); });
els.nextPageBtn.addEventListener("click", () => { void goToPage(state.currentPage + 1); });
// Page-number input: Enter to jump, Esc to revert.
els.pageInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
        const n = parseInt(els.pageInput.value, 10);
        if (Number.isFinite(n))
            void goToPage(n); // fire-and-forget: page-jump on Enter; goToPage logs its own errors
        els.pdfScroll.focus();
    }
    else if (ev.key === "Escape") {
        els.pageInput.value = String(state.currentPage);
        els.pdfScroll.focus();
    }
});
// Don't let arrow-key events bubble from the input to the canvas handler.
els.pageInput.addEventListener("keydown", (ev) => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(ev.key)) {
        ev.stopPropagation();
    }
});
// Sidebar mirror nav buttons (issue 10) — same handlers, shared state.
els.sbPrevPageBtn.addEventListener("click", () => { void goToPage(state.currentPage - 1); });
els.sbNextPageBtn.addEventListener("click", () => { void goToPage(state.currentPage + 1); });
els.sbPageInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
        const n = parseInt(els.sbPageInput.value, 10);
        if (Number.isFinite(n))
            void goToPage(n); // fire-and-forget: page-jump on Enter; goToPage logs its own errors
        els.pdfScroll.focus();
    }
    else if (ev.key === "Escape") {
        els.sbPageInput.value = String(state.currentPage);
        els.pdfScroll.focus();
    }
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(ev.key)) {
        ev.stopPropagation();
    }
});
// ─── Multi-page scroll mount ───────────────────────────────────────────────
/**
 * Build the multi-page frame scaffold inside #editor-pdf-pages and wire
 * MultiPageScrollManager for IntersectionObserver-like lazy canvas promotion.
 *
 * Must be called after state.pdfDoc is set and the workspace is visible
 * (so pdfScroll.clientWidth is non-zero for fitToWidthScale).
 *
 * Tears down any prior manager and per-page controllers before rebuilding
 * so re-opening a new file starts clean.
 */
export async function mountMultiPageScroll() {
    if (!state.pdfDoc || !els.pdfPages)
        return;
    // Tear down prior manager + per-page controllers.
    if (state.multiPageMgr) {
        try {
            state.multiPageMgr.destroy();
        }
        catch { /* best-effort */ }
        state.multiPageMgr = null;
    }
    state.multiPageReflow = null;
    for (const detach of (state.pageCtxDetachers || new Map()).values()) {
        try {
            if (detach)
                detach();
        }
        catch { /* best-effort */ }
    }
    state.pageCtxDetachers = new Map();
    for (const ctrl of state.dragMoveControllers.values()) {
        try {
            ctrl.destroy();
        }
        catch { /* best-effort */ }
    }
    state.dragMoveControllers.clear();
    if (state.drawCreateControllers) {
        for (const ctrl of state.drawCreateControllers.values()) {
            try {
                ctrl.destroy();
            }
            catch { /* best-effort */ }
        }
        state.drawCreateControllers.clear();
    }
    state.pageDescriptors = [];
    const totalPages = state.pdfDoc.numPages;
    // Async progressive sizing: fetch page 1 immediately to anchor scale +
    // first-page dimensions; estimate remaining pages from page 1's size.
    // The PDF.js worker is single-threaded, so awaiting all N getPage() calls
    // up front blocks first-paint by N × per-page-RTT (≈30 s for a 60-page
    // PDF). Page 1 unlocks the manager + first canvas render; the per-page
    // sizing fix-up (fixupPageSizes below) runs in the background and only
    // matters for cumulative scroll-height accuracy on pages the user
    // hasn't reached yet.
    const firstPage = await state.pdfDoc.getPage(1);
    const initScale = Number.isNaN(state.currentScale)
        ? fitToWidthScale(firstPage)
        : Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, state.currentScale));
    const firstVp = firstPage.getViewport({ scale: initScale });
    const estPageSize = { width: Math.floor(firstVp.width), height: Math.floor(firstVp.height) };
    const pageSizes = Array.from({ length: totalPages }, () => ({ ...estPageSize }));
    // Build DOM frames.
    const { frames, pageRects } = buildPageFrames(els.pdfPages, pageSizes);
    // For each frame, inject a <canvas> + overlay <div> replacing the stub
    // body. The DOM scaffold is built EAGERLY for every page so the
    // scroll container's geometry (cumulative height, scrollbar size) is
    // accurate at first paint. The four per-page controllers
    // (InsertFlow / DragMove / ResizeMove / RubberBand) are wired
    // EAGERLY only for page 1 (which the user is about to interact with);
    // pages 2..N defer controller wiring until first interaction need —
    // see _wirePageControllers below.
    for (let i = 0; i < frames.length; i++) {
        const frame = frames[i]; // WHY: loop invariant — i < frames.length
        const page = i + 1;
        // Remove stub placeholder.
        const stub = frame.querySelector(".pdf-page-stub");
        if (stub)
            stub.remove();
        const canvasEl = document.createElement("canvas"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        canvasEl.setAttribute("aria-label", `PDF page ${page}`);
        canvasEl.style.display = "block";
        frame.appendChild(canvasEl);
        const overlayEl = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        overlayEl.className = "editor-pdf-overlay";
        overlayEl.setAttribute("aria-hidden", "true");
        overlayEl.style.position = "absolute";
        overlayEl.style.top = "0";
        overlayEl.style.left = "0";
        overlayEl.style.width = "100%";
        overlayEl.style.height = "100%";
        frame.appendChild(overlayEl);
        state.pageDescriptors[i] = { frame, canvasEl, overlayEl };
    }
    // Tier 1: wire page-1 controllers synchronously. The user lands on
    // page 1; its insert/drag/resize/rubber-band gestures must be live
    // before hideLoading() fires.
    if (frames.length > 0) {
        _wirePageControllers(0, frames, pageSizes);
    }
    // Tier 2: wire pages 2..N in the background. requestIdleCallback (or
    // a setTimeout(0) shim) yields to the browser between pages so the
    // page-1 paint is never blocked by controller construction.
    if (frames.length > 1) {
        _scheduleDeferredControllerWiring(frames, pageSizes, state.lifecycle.generation);
    }
    // Wire MultiPageScrollManager.
    state.multiPageMgr = MultiPageScrollManager({
        scrollContainer: els.pdfScroll,
        frames,
        pageRects,
        renderCanvas: async (page, frame) => {
            // When the manager promotes a page to CANVAS zone, render the PDF.js
            // canvas. RETURN the Promise so the manager can mark the frame as
            // data-loading until the paint settles (CSS shows a centered
            // spinner over a translucent backdrop while the attribute is set)
            // and so scrollToPage(p) can await page-p's render.
            // eslint-disable-next-line promise/prefer-await-to-then -- .catch() transforms error-to-void; returned Promise is used by manager for timing; function is sync (return type is Promise, not async)
            return renderPageIntoFrame(page, frame).catch((err) => {
                log.warn(`[editor] multi-page renderCanvas page ${page} failed:`, err);
            });
        },
        renderOverlays: async (page, frame) => {
            // Render overlays for this page. The manager doesn't track this
            // Promise (overlays are independent of canvas paint), but we still
            // return it so callers can chain if needed.
            // eslint-disable-next-line promise/prefer-await-to-then -- .catch() transforms error-to-void; returned Promise is used by manager for timing; function is sync (return type is Promise, not async)
            return renderOverlaysForPage(page, frame).catch((err) => {
                log.warn(`[editor] multi-page renderOverlays page ${page} failed:`, err);
            });
        },
        demote: (page, frame) => {
            // Demote to stub — clear canvas content but keep the overlay in place
            // so inserted elements aren't wiped on scroll-away.
            const canvasEl = frame.querySelector("canvas");
            if (canvasEl) {
                const ctx = canvasEl.getContext("2d");
                if (ctx)
                    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
            }
            // Phase FB.11 (260524): release the bbox-cache entry for the
            // demoted page so the cache no longer grows monotonically. The
            // cache is a hint, not authoritative — viewport re-entry refetches
            // cheaply via getForPage. See
            // docs/design/frontend-mv-architecture-260524.md §P.7.
            _bboxCache.invalidatePages([page]);
        },
        onPageChange: (page) => {
            // Update current-page state and UI when the scrolled-to page changes.
            if (page !== state.currentPage && state.pdfDoc) {
                state.currentPage = page;
                updatePageNavControls();
                const numPages = state.pdfDoc.numPages;
                els.pageIndicator.textContent = `Page ${page} of ${numPages}`;
                els.statusPage.textContent = `Page ${page} / ${numPages}`;
                // Viewport-vicinity bbox prefetch (lazy-load redesign 260508
                // §3.2 phase 3): warm a window around the new visible page so
                // forward + backward scroll lands on cached overlays. The
                // cache dedups against entries already populated by the
                // post-Tier1 warmup or earlier vicinity calls — no
                // displacement.
                const lo = Math.max(1, page - 5);
                const hi = Math.min(totalPages, page + 15);
                void prefetchRange(lo, hi); // fire-and-forget: scroll-window bbox prefetch; errors logged in prefetchRange
            }
        },
    });
    // Doc-wide zoom reflow (bug4, docable-editor-overlay-render-260910).
    // `applyZoom` re-renders ONLY the current page; in multi-page mode every
    // other page's frame + canvas keeps its old scale, so a zoom appears to
    // affect just the active page. Store a closure over THIS scaffold's
    // frames/pageSizes/pageRects on `state` so `applyZoom` (editor-render.ts,
    // which cannot statically import this module — ESM cycle) can drive a
    // document-wide reflow. Captured `pageSizes` is the same array the per-page
    // controllers read, so mutating it in place keeps their gesture math in sync.
    state.multiPageReflow = () => reflowPagesToScale(frames, pageSizes, pageRects);
    // Background page-size fixup: resolve actual dimensions for pages 2..N
    // and update each frame's height in place. We rebuild pageRects so the
    // multi-page manager's visibility math reflects accurate cumulative
    // tops on later pages. This runs concurrently with the user scrolling /
    // editing — the initial estimate is good enough for the first paint and
    // for pages on the visible buffer.
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget background fixup; intentionally non-blocking to avoid delaying initial mount
    fixupPageSizes(totalPages, initScale, pageSizes, pageRects).catch((err) => {
        log.warn("[editor] async page-size fixup failed:", err);
    });
}
/**
 * Wire the four per-page interaction controllers (InsertFlow, DragMove,
 * ResizeMove, RubberBand) on a single page's overlay. Idempotent —
 * second calls are a no-op so the deferred-wiring path can be safely
 * triggered by both visibility-promotion and the eager-page-1 path
 * without double-wiring.
 *
 * The controllers are pure user-input handlers; until first interaction
 * on a page they cost nothing besides the construction time we defer
 * here. Pages 2..N's gestures are live the moment their controllers
 * are wired (not their canvas paint, which is a separate Tier-2 concern
 * driven by MultiPageScrollManager.renderCanvas).
 *
 * @param idx zero-based page index in `frames` / `pageSizes`.
 */
export function _wirePageControllers(idx, frames, pageSizes) {
    const cbs = _requireMultipageCbBindings();
    const page = idx + 1;
    if (state.drawCreateControllers.has(page))
        return; // idempotency guard
    const frame = frames[idx]; // WHY: caller contract — idx is validated by callers (frames.length > 0 check at call site)
    const overlayEl = frame.querySelector(".editor-pdf-overlay");
    if (!overlayEl)
        return;
    // Contextmenu on empty overlay area → rich openContextMenu (empty mode).
    // DrawCreateController (below) owns the drag-to-create path; the right-click
    // path is wired here directly so it reaches the active per-page overlay rather
    // than the hidden legacy #editor-pdf-overlay that wireContextMenu references.
    const onPageCtx = (ev) => {
        const t = ev.target;
        // Only fire on empty overlay area, not on existing bbox elements.
        if (t?.closest(".editor-overlay-bbox"))
            return;
        if (t !== overlayEl && !overlayEl.contains(t))
            return;
        ev.preventDefault();
        ev.stopPropagation();
        const rect = overlayEl.getBoundingClientRect();
        const clickPt = { x: (ev.clientX || 0) - rect.left, y: (ev.clientY || 0) - rect.top };
        openContextMenu({
            x: ev.clientX || 0,
            y: ev.clientY || 0,
            mode: "empty",
            onAction: (action, payload) => {
                const p = payload ?? {};
                void dispatchContextAction(action, { ...p, clickPt }); // fire-and-forget: context-menu action dispatch
            },
        });
    };
    overlayEl.addEventListener("contextmenu", onPageCtx);
    state.pageCtxDetachers.set(page, () => overlayEl.removeEventListener("contextmenu", onPageCtx));
    // DragMoveController — pointerdown on .editor-overlay-bbox starts drag.
    const dragCtrl = DragMoveController({
        overlayContainer: overlayEl,
        getSelected: () => state.selectedStructIds || new Set(),
        getBboxesById: () => buildPageBboxMap(state.ir, page),
        getScale: () => {
            const cssWidth = overlayEl.offsetWidth || 0;
            const sz = pageSizes[idx] ?? PDF_US_LETTER;
            return sz.width > 0 ? cssWidth / sz.width : 1;
        },
        getPageSize: () => {
            const sz = pageSizes[idx] ?? PDF_US_LETTER;
            return { width: sz.width, height: sz.height };
        },
        onSelectIfUnselected: (structId) => {
            // DragMoveController binds pointerdown on the per-page overlay
            // container and calls preventDefault() so the gesture can claim
            // the pointer for a potential drag. That preventDefault suppresses
            // the synthetic click event in Chromium / WebKit — the click
            // handler on .editor-overlay-bbox (overlays.js) never fires, so
            // the flash + scroll-into-view side effects of overlay onSelect
            // would be silently skipped on every overlay click. Mirror the
            // overlay onSelect single-click branch here so visual feedback
            // is preserved when click is preempted by the drag gesture.
            if (state.linkedViewsCtrl) {
                state.linkedViewsCtrl.selectStructId(structId, "single");
                state.linkedViewsCtrl.scrollCardIntoView(structId);
                state.linkedViewsCtrl.flashCard(structId);
            }
            else {
                cbs.selectStruct(structId);
            }
        },
        onCommit: (spec) => handleDragMoveCommit(spec),
    });
    state.dragMoveControllers.set(page, dragCtrl);
    // ResizeMoveController — pointerdown on .editor-resize-handle starts resize.
    const resizeCtrl = ResizeMoveController({
        overlayContainer: overlayEl,
        getBboxesById: () => buildPageBboxMap(state.ir, page),
        getScale: () => {
            const cssWidth = overlayEl.offsetWidth || 0;
            const sz = pageSizes[idx] ?? PDF_US_LETTER;
            return sz.width > 0 ? cssWidth / sz.width : 1;
        },
        getPageSize: () => {
            const sz = pageSizes[idx] ?? PDF_US_LETTER;
            return { width: sz.width, height: sz.height };
        },
        onCommit: (spec) => handleResizeCommit(spec),
    });
    state.resizeMoveControllers.set(page, resizeCtrl);
    // DrawCreateController — pointerdown in empty space starts snap-to-content
    // draw. Emits reassociate-mcids on commit when mcids are available; falls
    // back to a loose-rect draw when the IR carries no mcids for this page.
    const drawCtrl = DrawCreateController({
        overlayContainer: overlayEl,
        pageNum: page,
        getMcidsForPage: () => {
            if (!state.ir || !Array.isArray(state.ir.mcids))
                return [];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: state.ir.mcids is any-typed (wire IO shape); filter() returns any[]
            return state.ir.mcids.filter((m) => m?.page === page);
        },
        // Route the pixel→PDF conversion through the CANONICAL converter
        // (canvasBboxToPdf) — the SAME seam the right-click Insert path uses
        // (A.19). WHY (bug #2, 260813): the default converter divided by
        // `pageSizes` (the PDF.js viewport at initScale, i.e. scaled pixels), so
        // draw-created blocks landed in a scaled space while renderOverlays +
        // exportGroundTruth treat Block.bbox as TRUE PDF points — the drawn box
        // rendered off-screen and exported with wrong geometry. canvasBboxToPdf
        // reads the canvas's natural PDF-point dims (dataset.pageNaturalWidth/
        // Height), producing true points consistent with every consumer.
        pixelRectToPdf: (rect) => canvasBboxToPdf({ x: rect[0], y: rect[1], w: rect[2], h: rect[3] }, page),
        onCommit: (spec) => handleDrawCreateCommit(page, spec),
    });
    state.drawCreateControllers.set(page, drawCtrl);
    // RubberBandController — shift-drag in empty space.
    const rubberCtrl = RubberBandController({
        overlayContainer: overlayEl,
        getOverlayBoxes: () => {
            const out = [];
            for (const el of overlayEl.querySelectorAll(".editor-overlay-bbox")) {
                const id = el.dataset?.structId;
                if (!id)
                    continue;
                out.push({
                    structId: id,
                    x: parseFloat(el.style.left) || 0,
                    y: parseFloat(el.style.top) || 0,
                    w: parseFloat(el.style.width) || 0,
                    h: parseFloat(el.style.height) || 0,
                });
            }
            return out;
        },
        onCommit: (structIds) => {
            if (state.linkedViewsCtrl) {
                state.linkedViewsCtrl.setSelection(new Set(structIds), { keepAnchor: false });
            }
        },
    });
    state.rubberBandControllers.set(page, rubberCtrl);
}
/**
 * Schedule deferred wiring of pages 2..N's controllers via
 * requestIdleCallback (or setTimeout shim). Iterates pages in batches,
 * yielding to the browser between batches so neither the page-1 paint
 * nor user input is blocked. Aborts cleanly if the lifecycle generation
 * has rolled (file reset, new file dropped) — same race-protection
 * pattern as `_scheduleTier2Loading`.
 *
 * @param gen0 lifecycle generation captured at scheduling.
 */
function _scheduleDeferredControllerWiring(frames, pageSizes, gen0) {
    // Wire 8 pages per idle slice — pure JS construction is fast, but
    // batching gives the browser a chance to service input/paint between
    // slices on very large fixtures (60+ pages).
    const BATCH = 8;
    let cursor = 1; // page-1 (idx 0) already wired eagerly above
    const step = () => {
        if (state.lifecycle.generation !== gen0) {
            // File reset / new file dropped; abandon. The new load will
            // wire its own controllers from a fresh frame array.
            return;
        }
        const end = Math.min(frames.length, cursor + BATCH);
        for (; cursor < end; cursor++) {
            _wirePageControllers(cursor, frames, pageSizes);
        }
        if (cursor < frames.length) {
            _scheduleIdle(step);
        }
    };
    _scheduleIdle(step);
}
/**
 * Resolve the natural size of pages 2..N in batches and update each frame's
 * height in place. Rebuilds `state.multiPageMgr`'s pageRects so the visibility
 * classifier sees accurate cumulative tops as later pages resolve.
 *
 * Batching: PDF.js's worker handles requests serially, so a Promise.all over
 * 60 pages still serialises through the worker. The point of batching here is
 * cooperative yielding — we yield to the event loop after each chunk so the
 * UI thread stays responsive (scroll / type / select) while resizes land.
 */
export async function fixupPageSizes(totalPages, initScale, pageSizes, pageRects) {
    if (!state.pdfDoc || totalPages <= 1)
        return;
    const BATCH = 8;
    let dirty = false;
    for (let start = 2; start <= totalPages; start += BATCH) {
        const end = Math.min(totalPages, start + BATCH - 1);
        const pages = await Promise.all(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: mountMultiPageScroll only called after pdfDoc is assigned in loadFile Tier-1
        Array.from({ length: end - start + 1 }, (_, k) => state.pdfDoc.getPage(start + k)));
        for (let k = 0; k < pages.length; k++) {
            const pageNum = start + k;
            const idx = pageNum - 1;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: k < pages.length guarantees pages[k] is defined
            const vp = pages[k].getViewport({ scale: initScale });
            const w = Math.floor(vp.width);
            const h = Math.floor(vp.height);
            const cur = pageSizes[idx];
            if (cur?.width === w && cur.height === h)
                continue;
            pageSizes[idx] = { width: w, height: h };
            // Update the frame's height in place; the manager's pageRects map
            // is rebuilt below so visibility math sees the new tops.
            const desc = state.pageDescriptors[idx];
            if (desc?.frame) {
                desc.frame.style.width = `${w}px`;
                desc.frame.style.height = `${h}px`;
            }
            dirty = true;
        }
        // Yield to the event loop after each batch so the user can scroll /
        // type while the rest resolve.
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (!dirty || !state.multiPageMgr)
        return;
    // Recompute cumulative tops for pageRects so the visibility classifier
    // sees accurate positions now that later pages may differ from the
    // page-1-based estimate. The manager holds a live reference to this
    // Map; mutating it in-place + refresh() reclassifies zones.
    if (pageRects && typeof pageRects.set === "function") {
        let cumTop = 0;
        const GAP = 24;
        const LABEL_H = 16;
        for (let p = 1; p <= totalPages; p++) {
            const sz = pageSizes[p - 1] ?? PDF_US_LETTER;
            pageRects.set(p, { top: cumTop, height: sz.height });
            cumTop += sz.height + GAP + LABEL_H;
        }
    }
    if (typeof state.multiPageMgr.refresh === "function") {
        state.multiPageMgr.refresh();
    }
}
/**
 * Document-wide zoom reflow (bug4, docable-editor-overlay-render-260910).
 *
 * `applyZoom` sets `state.currentScale` and re-renders ONLY the current page.
 * In multi-page mode every OTHER page frame keeps the pixel dimensions it was
 * built with (page-1-anchored estimate → `fixupPageSizes` at the mount scale),
 * so a zoom visibly rescales just the active page while the rest stay put. This
 * reflow closes that gap: it resizes EVERY page frame to the new scale, rebuilds
 * the scroll geometry (`pageRects`), and re-paints every currently-promoted
 * (CANVAS / OVERLAYS) page so its canvas + overlays match the new scale.
 *
 * The current page is skipped in the re-paint pass — `applyZoom` already
 * re-rendered it via `renderPage`; re-painting it here would double the work and
 * race two render tasks onto the same canvas. Its frame IS resized in the
 * geometry pass so cumulative scroll tops stay correct.
 *
 * Scale resolution mirrors `mountMultiPageScroll`: NaN `currentScale` means
 * fit-to-width (per-page), else the clamped explicit scale. Frames are floored
 * to whole pixels, matching `buildPageFrames` / `fixupPageSizes`.
 *
 * @param frames    the per-page frame elements (1-based via index 0).
 * @param pageSizes the mutable pixel-size array shared with the per-page
 *                  controllers — mutated in place so their gesture math stays
 *                  registered with the resized frames.
 * @param pageRects the manager's live layout map — mutated + `refresh()`ed.
 */
export async function reflowPagesToScale(frames, pageSizes, pageRects) {
    if (!state.pdfDoc || !state.multiPageMgr)
        return;
    const totalPages = state.pdfDoc.numPages;
    const explicit = !Number.isNaN(state.currentScale);
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, state.currentScale));
    // Geometry pass: resize every frame to the new scale (batched with event-loop
    // yields so a large fixture doesn't jank the zoom gesture).
    const BATCH = 8;
    const promoted = [];
    for (let start = 1; start <= totalPages; start += BATCH) {
        const end = Math.min(totalPages, start + BATCH - 1);
        const pages = await Promise.all(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: state.pdfDoc asserted non-null above (guard at function top)
        Array.from({ length: end - start + 1 }, (_, k) => state.pdfDoc.getPage(start + k)));
        for (let k = 0; k < pages.length; k++) {
            const pageNum = start + k;
            const idx = pageNum - 1;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: k < pages.length guarantees pages[k] is defined
            const page = pages[k];
            const pageScale = explicit ? clamped : fitToWidthScale(page, pageNum);
            const vp = page.getViewport({ scale: pageScale });
            const w = Math.floor(vp.width);
            const h = Math.floor(vp.height);
            pageSizes[idx] = { width: w, height: h };
            const desc = state.pageDescriptors[idx];
            if (desc?.frame) {
                desc.frame.style.width = `${w}px`;
                desc.frame.style.height = `${h}px`;
                const zone = desc.frame.dataset.zone;
                if (zone === PageZone.CANVAS || zone === PageZone.OVERLAYS) {
                    promoted.push(pageNum);
                }
            }
        }
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
    // Rebuild cumulative scroll tops for the new frame sizes, then reclassify.
    let cumTop = 0;
    const GAP = 24;
    const LABEL_H = 16;
    for (let p = 1; p <= totalPages; p++) {
        const sz = pageSizes[p - 1] ?? PDF_US_LETTER;
        pageRects.set(p, { top: cumTop, height: sz.height });
        cumTop += sz.height + GAP + LABEL_H;
    }
    if (typeof state.multiPageMgr.refresh === "function")
        state.multiPageMgr.refresh();
    // Re-paint every promoted page (except the current page, already re-rendered
    // by applyZoom's renderPage) so its canvas + overlays match the new scale.
    for (const pageNum of promoted) {
        if (pageNum === state.currentPage)
            continue;
        const frame = frames[pageNum - 1];
        if (!frame)
            continue;
        try {
            await renderPageIntoFrame(pageNum, frame);
            const zone = state.pageDescriptors[pageNum - 1]?.frame?.dataset.zone;
            if (zone === PageZone.OVERLAYS)
                await renderOverlaysForPage(pageNum, frame);
        }
        catch (err) {
            log.warn(`[editor] doc-wide zoom reflow: page ${pageNum} re-render failed:`, err);
        }
    }
}
